import React from 'react';
import { useI18n } from '../../context/I18nContext';
import Button from './Button';
import Spinner from './Spinner';

interface TableCardContentProps {
  error?: string | null;
  /** From useTableInitialLoading — blocks body only when list has never been shown yet. */
  showInitialLoading?: boolean;
  onRetry?: () => void;
  children: React.ReactNode;
}

/**
 * Shared table card body: error / initial spinner / table content.
 * Keeps table headers mounted once data has loaded (no flicker on filter refresh).
 */
const TableCardContent: React.FC<TableCardContentProps> = ({
  error,
  showInitialLoading = false,
  onRetry,
  children,
}) => {
  const { t } = useI18n();

  if (error) {
    return (
      <div className="p-6 text-center">
        <p className="font-medium text-red-600">{t('common.error')}</p>
        <p className="mt-1 text-gray-500">{error}</p>
        {onRetry ? (
          <Button variant="primary" className="mt-4" onClick={onRetry}>
            {t('common.retry')}
          </Button>
        ) : null}
      </div>
    );
  }

  if (showInitialLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner size="lg" label={t('common.loading')} />
      </div>
    );
  }

  return <>{children}</>;
};

export default TableCardContent;
